import { memo, type FC, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AI_TUTOR_BRAND_NAME, useAiTutorStatusConfig } from 'TutorBotAiTutorChat';
import { Chat, getActiveConversationMessages } from 'TutorBotChat';
import { type StructuredMessageHandler } from 'TutorBotChat/types/StructuredMessage.types';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { useFooterContent, useLessonPlayerScrollContainer, useFrameViewModel } from 'Lessons';
import { type FreeResponseFrameViewModel } from './FreeResponseFrameViewModel';
import './locales/free_response/free_response-en.json';

interface Props {
    frameViewModel: FreeResponseFrameViewModel;
}

const FreeResponsePlayerUiComponent: FC<Props> = ({ frameViewModel }) => {
    const currentUser = useRequiredCurrentUser();
    const dispatch = useDispatch();
    const { t } = useTranslation('back_royal', { keyPrefix: 'free_response.free_response' });
    const botName = AI_TUTOR_BRAND_NAME;
    const tutorBotStatusConfig = useAiTutorStatusConfig({
        botName,
        disallowWithInProgressExam: false,
        enableWelcomeModals: false,
    });
    const conversationMessages = useSelector(getActiveConversationMessages);
    const scrollWrapper = useLessonPlayerScrollContainer();

    const continueButtonVisible = useFrameViewModel(frameViewModel, vm => vm.continueButtonVisible);

    // useFooterContent gives us access to a portal element in the footer of the frame player.
    // We pass that in as the chatControls.container to the Chat element, and then Chat renders
    // the chat user input in the footer
    const chatControlsContainer = useFooterContent();

    useLayoutEffect(() => {
        frameViewModel.onMessageUpdate();
    }, [frameViewModel, conversationMessages]);

    useEffect(() => {
        frameViewModel.ensureConversationStarted(dispatch);
    }, [dispatch, frameViewModel]);

    const handleStructuredMessage: StructuredMessageHandler = useCallback(
        (payload: { type: string; [key: string]: unknown }) => {
            frameViewModel.onStructuredMessage(payload);
        },
        [frameViewModel],
    );

    const handleMessageTextChange = useCallback(
        (text: string) => {
            frameViewModel.unsentMessageText = text;
        },
        [frameViewModel],
    );

    const showOfflineModeMessage = frameViewModel.showOfflineModeMessage;
    const chatControls = useMemo(
        () => ({
            container: chatControlsContainer,

            // When the continue button is visible on mobile, the chatControlsContainer (user input) gets pushed
            // up and will overlap the bottom of the chat messages if we don't give it a little space at the bottom.
            xsBottomPadding: continueButtonVisible ? 12.5 : undefined,
        }),
        [chatControlsContainer, continueButtonVisible],
    );

    return (
        // This frame has to take up the full space so that the first message starts
        // at the bottom of the screen, as expected with our <Chat> interfaces
        <div className="flex h-full min-h-0 w-full">
            {showOfflineModeMessage ? (
                <div className="flex w-full items-center justify-center p-6 text-center text-base">
                    {t('offline_message')}
                </div>
            ) : (
                frameViewModel.initialMessage &&
                scrollWrapper && (
                    <Chat
                        currentUser={currentUser}
                        clientContext={frameViewModel.clientContext}
                        tutorBotStatusConfig={tutorBotStatusConfig}
                        botName={botName}
                        initialMessage={frameViewModel.initialMessage}
                        disable={frameViewModel.editorMode}
                        chatControls={chatControls}
                        onStructuredMessage={handleStructuredMessage}
                        onMessageTextChange={handleMessageTextChange}
                        // There is not room for this message at the bottom of the player, and if we don't hide it
                        // it sometimes appears for a moment during button navigations on mobile.
                        showCanMakeMistakesMessage={false}
                        scrollElement={scrollWrapper}
                    />
                )
            )}
        </div>
    );
};

export const FreeResponsePlayerUi = memo(FreeResponsePlayerUiComponent) as typeof FreeResponsePlayerUiComponent;
